import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { API_autoschedule_eval_runs } from "../../../../../constants";
import { get_t_str, getCardColor2 } from "../../../../../helpers/helpers";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../../../requests/utilities/requests";
import { getBusinessWeekStartDayIndex } from "../../dateRangePresets";
import WeekSelector from "../../../home/top_bar_components/WeekSelector";
import PrebuiltReportShell from "../PrebuiltReportShell";
import { unwrapPayload } from "./_shared/laborMath";

const RUN_METRIC_DEFINITIONS = [
  {
    key: "recommendation_quality_score",
    label: "Recommendation Quality",
    explanation:
      "Fixed evaluation score combining coverage, budget, role targets, and leadership coverage.",
  },
  {
    key: "coverage_score",
    label: "Coverage",
    explanation: "Intraday labor coverage versus the forecasted staffing target.",
  },
  {
    key: "budget_score",
    label: "Budget Fit",
    explanation: "Scheduled labor hours versus forecasted labor hours.",
  },
  {
    key: "role_target_fit_score",
    label: "Role Targets",
    explanation: "Intraday fit to learned role-target labor splits by scheduled main role.",
  },
  {
    key: "target_hours_score",
    label: "Target Hours",
    explanation: "Employee scheduled-hours fit to target hours.",
  },
  {
    key: "schedule_consistency_score",
    label: "Schedule Stability",
    explanation: "Employee day-count consistency versus recent history.",
  },
  {
    key: "shift_consistency_score",
    label: "Shift Time Consistency",
    explanation: "Employee shift-time consistency versus recent history.",
  },
  {
    key: "opening_closing_consistency_score",
    label: "Open/Close Stability",
    explanation: "Opener and closer consistency versus recent history.",
  },
  {
    key: "minimum_staffing_coverage_percent",
    label: "Min Staffing",
    explanation: "Share of active periods meeting the configured staffing floor.",
    hideWhenNoSignal: true,
  },
  {
    key: "keyholder_coverage_percent",
    label: "Keyholder",
    explanation: "Active scheduled time covered by keyholder-capable employees.",
    hideWhenNoSignal: true,
    requiresKeyholder: true,
  },
];

const RUN_METRIC_DEFINITION_BY_KEY = RUN_METRIC_DEFINITIONS.reduce((map, metric) => {
  map[metric.key] = metric;
  return map;
}, {});

const CORE_METRIC_KEYS = [
  "recommendation_quality_score",
  "coverage_score",
  "budget_score",
  "role_target_fit_score",
  "keyholder_coverage_percent",
];

const DIAGNOSTIC_METRIC_KEYS = [
  "target_hours_score",
  "schedule_consistency_score",
  "shift_consistency_score",
  "opening_closing_consistency_score",
  "minimum_staffing_coverage_percent",
];

const CHART_METRICS = [
  { key: "recommendation_quality_score", name: "Quality", color: "#0f766e" },
  { key: "role_target_fit_score", name: "Role fit", color: "#2563eb" },
  { key: "coverage_score", name: "Coverage", color: "#7c3aed" },
  { key: "budget_score", name: "Budget", color: "#ca8a04" },
];

const STATUS_TONES = {
  completed: "bg-emerald-100 text-emerald-700",
  running: "bg-blue-100 text-blue-700",
  failed: "bg-rose-100 text-rose-700",
};

const SOURCE_LABELS = {
  published_schedule: "Published",
  actual_used_schedule: "Worked",
  actual_schedule: "Draft",
};

const EVAL_ROLE_COLOR_CLASSES = [
  "bg-violet-600",
  "bg-sky-700",
  "bg-cyan-600",
  "bg-emerald-600",
  "bg-lime-600",
  "bg-amber-500",
  "bg-orange-600",
  "bg-red-600",
  "bg-pink-600",
];

function snapToWeekStart(date, weekStartDayIndex) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const diff = (d.getDay() - weekStartDayIndex + 7) % 7;
  d.setDate(d.getDate() - diff);
  return d;
}

function getWeekAnchorDate(weekStart) {
  const d = new Date(weekStart);
  d.setDate(d.getDate() + 3);
  return d;
}

function coerceNumber(value) {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
}

function formatNumber(value, digits = 1) {
  const numeric = coerceNumber(value);
  if (numeric == null) return "-";
  return numeric.toFixed(digits);
}

function formatDelta(value, digits = 1) {
  const numeric = coerceNumber(value);
  if (numeric == null) return "-";
  const prefix = numeric > 0 ? "+" : "";
  return `${prefix}${numeric.toFixed(digits)}`;
}

// Axis ticks keep the decimals Recharts' tick step has (0.25, not "+0").
function formatAxisDelta(value) {
  const numeric = coerceNumber(value);
  if (numeric == null) return "";
  const trimmed = Number(numeric.toFixed(2));
  return `${trimmed > 0 ? "+" : ""}${trimmed}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("M/D h:mm A") : "-";
}

function shortHash(value) {
  if (!value) return "-";
  return String(value).slice(0, 10);
}

function getDeltaTone(value, higherIsBetter = true) {
  const numeric = coerceNumber(value);
  if (numeric == null || Math.abs(numeric) < 0.0001 || higherIsBetter == null) {
    return "text-slate-600";
  }
  const direction = higherIsBetter ? numeric : -numeric;
  return direction > 0 ? "text-emerald-700" : "text-rose-700";
}

function getDeltaBadgeTone(value, higherIsBetter = true) {
  const tone = getDeltaTone(value, higherIsBetter);
  if (tone === "text-emerald-700") return "bg-emerald-50 text-emerald-700";
  if (tone === "text-rose-700") return "bg-rose-50 text-rose-700";
  return "bg-slate-100 text-slate-600";
}

function getForecastDeltaTone(value) {
  const numeric = coerceNumber(value);
  if (numeric == null) return "text-slate-600";
  if (Math.abs(numeric) < 0.05) return "text-emerald-700";
  return numeric > 0 ? "text-rose-700" : "text-amber-700";
}

function getStatusTone(status) {
  return STATUS_TONES[status] || "bg-slate-100 text-slate-600";
}

function getRoleTargetWeight(run) {
  const weight = run?.config?.role_target_fit_weight;
  return weight == null ? "default" : formatNumber(weight, 2);
}

function getRunName(run, index = 0) {
  if (!run) return "-";
  return run.run_label || `Run ${index + 1}`;
}

function getErrorMessage(err, fallback) {
  return (
    err?.response?.data?.redux?.payload?.error ||
    err?.response?.data?.error ||
    err?.response?.data?.message ||
    fallback
  );
}

function countBy(rows, key) {
  return (rows || []).reduce((acc, row) => {
    const value = row?.[key] || "unknown";
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
}

function clampPercent(value) {
  const numeric = coerceNumber(value);
  if (numeric == null) return 0;
  return Math.max(0, Math.min(100, numeric));
}

function getNullablePercent(value) {
  const numeric = coerceNumber(value);
  if (numeric == null) return null;
  return Math.max(0, Math.min(100, numeric));
}

function averageScores(scores) {
  const numericScores = (scores || [])
    .map((score) => coerceNumber(score))
    .filter((score) => score != null);
  if (!numericScores.length) return null;
  return numericScores.reduce((total, score) => total + score, 0) / numericScores.length;
}

function getScoreGrade(score) {
  const numeric = coerceNumber(score);
  if (numeric == null) return "-";
  if (numeric >= 90) return "A";
  if (numeric >= 80) return "B";
  if (numeric >= 70) return "C";
  if (numeric >= 60) return "D";
  return "F";
}

function getScoreGradeClass(score) {
  const grade = getScoreGrade(score);
  if (grade === "A") return "bg-emerald-600 text-white";
  if (grade === "B") return "bg-lime-600 text-white";
  if (grade === "C") return "bg-amber-500 text-white";
  if (grade === "D") return "bg-orange-600 text-white";
  if (grade === "F") return "bg-rose-600 text-white";
  return "bg-slate-400 text-white";
}

function formatPercent(value, digits = 1) {
  const numeric = coerceNumber(value);
  if (numeric == null) return "-";
  return `${numeric.toFixed(digits)}%`;
}

function getScheduleRowHours(row) {
  const hours =
    coerceNumber(row?.hours) ??
    coerceNumber(row?.shift_length) ??
    Math.max(
      0,
      (coerceNumber(row?.shift_end) || 0) -
        (coerceNumber(row?.shift_start) || 0) -
        (coerceNumber(row?.break_length) || 0) -
        (coerceNumber(row?.break_length_2) || 0)
    );
  return hours || 0;
}

function sumRowsHours(rows) {
  return (rows || []).reduce((total, row) => total + getScheduleRowHours(row), 0);
}

function getLaborRowHours(row) {
  const hours = coerceNumber(row?.hours);
  if (hours != null) return hours;
  const labor = coerceNumber(row?.labor);
  return labor == null ? 0 : labor / 4;
}

function getLaborRowValue(row) {
  const labor = coerceNumber(row?.labor);
  if (labor != null) return labor;
  const hours = coerceNumber(row?.hours);
  return hours == null ? 0 : hours * 4;
}

function getForecastLaborValue(row) {
  const hours = coerceNumber(row?.hours);
  if (hours != null) return hours * 4;
  return coerceNumber(row?.labor) || 0;
}

function sumLaborHours(rows, laborType, date = null) {
  return (rows || [])
    .filter((row) => (
      row.labor_type === laborType &&
      (!date || row.date === date)
    ))
    .reduce((total, row) => total + getLaborRowHours(row), 0);
}

function formatDateLabel(value) {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("ddd M/D") : value;
}

function cleanDisplayText(value) {
  const text = String(value || "").trim();
  return ["none", "nan", "null"].includes(text.toLowerCase()) ? "" : text;
}

function formatTime(value) {
  const numeric = coerceNumber(value);
  if (numeric == null) return "-";
  return get_t_str(numeric);
}

function formatShiftRange(row) {
  return `${formatTime(row?.shift_start)} - ${formatTime(row?.shift_end)}`;
}

function formatCompactShiftRange(row) {
  return formatShiftRange(row)
    .replace(/:00/g, "")
    .replace(/am/g, "a")
    .replace(/pm/g, "p");
}

function buildInitials(name) {
  if (!name) return "";
  const parts = String(name).split(" ").filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function buildWeekDays(run) {
  if (!run?.week_start) return [];
  return Array.from({ length: 7 }, (_, index) => {
    const date = dayjs(run.week_start).add(index, "day").format("YYYY-MM-DD");
    return {
      date,
      label: formatDateLabel(date),
    };
  });
}

function getScheduleRowsForDate(run, scheduleType, date) {
  return (run?.schedule_rows || [])
    .filter((row) => row.schedule_type === scheduleType && row.date === date)
    .slice()
    .sort((a, b) => (
      (coerceNumber(a.shift_start) || 0) - (coerceNumber(b.shift_start) || 0) ||
      cleanDisplayText(a.person).localeCompare(cleanDisplayText(b.person))
    ));
}

function getMemberKey(row) {
  const memberId = coerceNumber(row?.store_team_member_id);
  if (memberId != null && memberId > 0) return `member:${memberId}`;
  return `person:${getScheduleRowPersonLabel(row)}`;
}

function getScheduleRowPersonLabel(row) {
  const person = cleanDisplayText(row?.person);
  if (person) return person;
  const memberId = coerceNumber(row?.store_team_member_id);
  if (memberId != null && memberId > 0) return `Employee #${memberId}`;
  return "Open shift";
}

function getScheduleRowMemberId(row) {
  return coerceNumber(row?.store_team_member_id);
}

function isOpenScheduleRow(row) {
  if (row?.status === "AVAILABLE") return true;
  const memberId = getScheduleRowMemberId(row);
  return (memberId == null || memberId < 0) && !cleanDisplayText(row?.person);
}

function getScheduleRowRoleId(row) {
  return (
    coerceNumber(row?.role_id) ??
    coerceNumber(row?.role_id_id) ??
    coerceNumber(row?.scheduled_role_id)
  );
}

function getScheduleRowScoringRoleId(row) {
  return coerceNumber(row?.scoring_role_id) ?? getScheduleRowRoleId(row);
}

function getScheduleRowRoleLabel(row) {
  return (
    cleanDisplayText(row?.role_name) ||
    cleanDisplayText(row?.role_type) ||
    "No role"
  );
}

function getScheduleRowScoringRoleLabel(row) {
  return (
    cleanDisplayText(row?.scoring_role_name) ||
    getScheduleRowRoleLabel(row)
  );
}

function getRoleTargetDisplayLabel(row) {
  const roleName = getScheduleRowScoringRoleLabel(row);
  if (roleName) return roleName;
  const roleId = getScheduleRowScoringRoleId(row);
  if (roleId != null) return `Role ${roleId}`;
  return cleanDisplayText(row?.role_type) || "No role";
}

function getRoleTargetDisplayKey(row) {
  const roleId = getScheduleRowScoringRoleId(row);
  if (roleId != null) return `role:${roleId}`;
  const roleName = getScheduleRowScoringRoleLabel(row);
  if (roleName) return `name:${roleName.toLowerCase()}`;
  const roleType = cleanDisplayText(row?.role_type);
  if (roleType) return `type:${roleType.toLowerCase()}`;
  return "none";
}

function buildRoleColorLookups(run) {
  const roleMap = new Map();
  const typeSet = new Set();
  const allRows = [
    ...(run?.schedule_rows || []),
    ...(run?.labor_rows || []),
  ];

  allRows.forEach((row) => {
    const roleId = getScheduleRowRoleId(row);
    const roleLabel = getScheduleRowRoleLabel(row);
    if (roleLabel && roleLabel !== "No role") {
      typeSet.add(roleLabel);
    }
    if (roleId == null || roleMap.has(roleId)) {
      return;
    }
    roleMap.set(roleId, {
      id: roleId,
      role_name: roleLabel === "No role" ? `Role ${roleId}` : roleLabel,
    });
  });

  const roleNames = Array.from(roleMap.values())
    .sort((a, b) => a.role_name.localeCompare(b.role_name))
    .map((role, index) => ({
      ...role,
      color: EVAL_ROLE_COLOR_CLASSES[index % EVAL_ROLE_COLOR_CLASSES.length],
    }));

  const roleTypeColors = {};
  roleNames.forEach((role) => {
    if (role.role_name) {
      roleTypeColors[role.role_name] = role.color;
    }
  });

  Array.from(typeSet)
    .sort()
    .forEach((roleType) => {
      if (roleTypeColors[roleType]) {
        return;
      }
      const index = Object.keys(roleTypeColors).length;
      roleTypeColors[roleType] =
        EVAL_ROLE_COLOR_CLASSES[index % EVAL_ROLE_COLOR_CLASSES.length];
    });

  return { roleNames, roleTypeColors };
}

function getScheduleRowRoleColorClass(row, roleColorLookups) {
  const roleId = getScheduleRowRoleId(row);
  if (roleId != null) {
    return getCardColor2(roleId, roleColorLookups?.roleNames || [], false, false);
  }
  return (
    roleColorLookups?.roleTypeColors?.[getScheduleRowRoleLabel(row)] ||
    "bg-slate-600"
  );
}

function getShiftStart(row) {
  return coerceNumber(row?.shift_start);
}

function getShiftEnd(row) {
  return coerceNumber(row?.shift_end);
}

function getShiftDuration(row) {
  const start = getShiftStart(row);
  const end = getShiftEnd(row);
  if (start != null && end != null && end > start) {
    return end - start;
  }
  return coerceNumber(row?.shift_length) || getScheduleRowHours(row);
}

function getShiftExactKey(row) {
  return [
    getMemberKey(row),
    row?.date || "",
    row?.role_id || row?.role_type || "",
    coerceNumber(row?.shift_start) ?? "",
    coerceNumber(row?.shift_end) ?? "",
  ].join("|");
}

function getShiftChangeType(row, counterpartRows, scheduleType) {
  const counterpartExactKeys = new Set(
    (counterpartRows || []).map((counterpart) => getShiftExactKey(counterpart))
  );
  if (counterpartExactKeys.has(getShiftExactKey(row))) return "same";

  const counterpartMemberKeys = new Set(
    (counterpartRows || []).map((counterpart) => getMemberKey(counterpart))
  );
  if (counterpartMemberKeys.has(getMemberKey(row))) return "changed";
  return scheduleType === "recommended" ? "added" : "removed";
}

function getRoleTargetRowsForDate(run, date) {
  return (run?.labor_rows || []).filter(
    (row) => row.labor_type === "role_optimal" && row.date === date
  );
}

function getRoleTargetKey(row) {
  const roleId = getScheduleRowScoringRoleId(row);
  if (roleId != null) return `role:${roleId}`;
  return `label:${getScheduleRowScoringRoleLabel(row)}`;
}

function isShiftActiveAtQh(row, qh) {
  const start = getShiftStart(row);
  const end = getShiftEnd(row);
  if (start == null || end == null || qh < start || qh >= end) {
    return false;
  }
  const breakStart = coerceNumber(row?.break_start);
  const breakLength = coerceNumber(row?.break_length) || 0;
  if (breakLength > 0 && breakStart != null && qh >= breakStart && qh < breakStart + breakLength) {
    return false;
  }
  const breakStart2 = coerceNumber(row?.break_start_2);
  const breakLength2 = coerceNumber(row?.break_length_2) || 0;
  if (breakLength2 > 0 && breakStart2 != null && qh >= breakStart2 && qh < breakStart2 + breakLength2) {
    return false;
  }
  return true;
}

function getDailyRoleTargetBreakdown(run, scheduleType, date) {
  const summaryKey = scheduleType === "actual" ? "old_sched" : "new_sched";
  const rows = run?.role_target_metrics?.[summaryKey]?.role_target_breakdown || [];
  return rows.filter((row) => row?.date === date);
}

function sumCompleteMetric(rows, metricKey) {
  if (!rows.length) return null;
  const values = rows.map((row) => coerceNumber(row?.[metricKey]));
  if (values.some((value) => value == null)) return null;
  return values.reduce((total, value) => total + value, 0);
}

function getDailyRoleTargetMisses(run, date, actualRows, recommendedRows, targetRows) {
  const actualBreakdown = getDailyRoleTargetBreakdown(run, "actual", date);
  const recommendedBreakdown = getDailyRoleTargetBreakdown(
    run,
    "recommended",
    date
  );
  const persistedMetrics = [
    ["weighted_error_hours", false],
    ["absolute_error_hours", true],
  ];
  for (let index = 0; index < persistedMetrics.length; index += 1) {
    const [metricKey, isDiagnostic] = persistedMetrics[index];
    const actualHours = sumCompleteMetric(actualBreakdown, metricKey);
    const recommendedHours = sumCompleteMetric(recommendedBreakdown, metricKey);
    if (actualHours != null && recommendedHours != null) {
      return { actualHours, recommendedHours, isDiagnostic };
    }
  }
  const roleDiagnostics = buildDailyRoleTargetOptions({
    actualRows,
    recommendedRows,
    roleTargetRows: targetRows,
  });
  const hasTarget = roleDiagnostics.some((role) => role.targetHours > 0);
  return {
    actualHours: hasTarget
      ? roleDiagnostics.reduce((total, role) => total + role.actualMissHours, 0)
      : null,
    recommendedHours: hasTarget
      ? roleDiagnostics.reduce((total, role) => total + role.recommendedMissHours, 0)
      : null,
    isDiagnostic: true,
  };
}

function hasRunSnapshotRows(run) {
  return Boolean((run?.schedule_rows || []).length && (run?.labor_rows || []).length);
}

function buildDailyRoleTargetOptions(daySummary) {
  if (!daySummary) return [];
  const roles = new Map();

  function ensureRole(roleKey, row) {
    if (!roles.has(roleKey)) {
      roles.set(roleKey, {
        roleKey,
        roleLabel: getRoleTargetDisplayLabel(row),
        exactRoleKeys: new Set(),
        byQh: new Map(),
      });
    }
    const role = roles.get(roleKey);
    role.exactRoleKeys.add(getRoleTargetKey(row));
    const roleLabel = getRoleTargetDisplayLabel(row);
    if (roleLabel && role.roleLabel.startsWith("Role ")) {
      role.roleLabel = roleLabel;
    }
    return role;
  }

  function ensureQh(role, qhIndex) {
    if (!role.byQh.has(qhIndex)) {
      role.byQh.set(qhIndex, {
        qhIndex,
        qh: qhIndex / 4,
        label: formatTime(qhIndex / 4),
        target: 0,
        actual: 0,
        recommended: 0,
      });
    }
    return role.byQh.get(qhIndex);
  }

  (daySummary.roleTargetRows || []).forEach((targetRow) => {
    const qh = coerceNumber(targetRow?.qh_num);
    const targetHeadcount = coerceNumber(targetRow?.target_headcount);
    if (qh == null || targetHeadcount == null || targetHeadcount <= 0) return;
    const roleKey = getRoleTargetDisplayKey(targetRow);
    const role = ensureRole(roleKey, targetRow);
    ensureQh(role, Math.round(qh * 4)).target += targetHeadcount;
  });

  function addScheduleRows(rows, scheduleKey) {
    (rows || []).forEach((scheduleRow) => {
      const start = getShiftStart(scheduleRow);
      const end = getShiftEnd(scheduleRow);
      if (start == null || end == null || end <= start) return;
      const roleKey = getRoleTargetDisplayKey(scheduleRow);
      const role = ensureRole(roleKey, scheduleRow);
      for (
        let qhIndex = Math.round(start * 4);
        qhIndex < Math.round(end * 4);
        qhIndex += 1
      ) {
        const qh = qhIndex / 4;
        if (!isShiftActiveAtQh(scheduleRow, qh)) continue;
        ensureQh(role, qhIndex)[scheduleKey] += 1;
      }
    });
  }

  addScheduleRows(daySummary.actualRows, "actual");
  addScheduleRows(daySummary.recommendedRows, "recommended");

  function findPersistedBreakdown(rows, role) {
    const keys = new Set([role.roleKey, ...role.exactRoleKeys]);
    return (rows || []).find((row) => (
      keys.has(getRoleTargetDisplayKey(row)) ||
      keys.has(getRoleTargetKey(row))
    ));
  }

  return Array.from(roles.values())
    .map((role) => {
      const rawRows = Array.from(role.byQh.values())
        .filter((row) => row.target > 0 || row.actual > 0 || row.recommended > 0)
        .sort((a, b) => a.qhIndex - b.qhIndex);
      let targetTotal = 0;
      let actualError = 0;
      let recommendedError = 0;
      let actualScheduledTotal = 0;
      let recommendedScheduledTotal = 0;
      rawRows.forEach((row) => {
        targetTotal += row.target;
        actualScheduledTotal += row.actual;
        recommendedScheduledTotal += row.recommended;
        actualError += Math.abs(row.target - row.actual);
        recommendedError += Math.abs(row.target - row.recommended);
      });
      const firstActive = rawRows[0];
      const lastActive = rawRows[rawRows.length - 1];
      let chartData = rawRows;
      if (firstActive && lastActive) {
        const startIndex = Math.max(0, firstActive.qhIndex - 2);
        const endIndex = lastActive.qhIndex + 2;
        chartData = Array.from({ length: endIndex - startIndex + 1 }, (_, index) => {
          const qhIndex = startIndex + index;
          return role.byQh.get(qhIndex) || {
            qhIndex,
            qh: qhIndex / 4,
            label: formatTime(qhIndex / 4),
            target: 0,
            actual: 0,
            recommended: 0,
          };
        });
      }
      const actualBreakdown = findPersistedBreakdown(
        daySummary.actualRoleTargetBreakdown,
        role
      );
      const recommendedBreakdown = findPersistedBreakdown(
        daySummary.recommendedRoleTargetBreakdown,
        role
      );
      const actualWeightedError = coerceNumber(actualBreakdown?.weighted_error_hours);
      const recommendedWeightedError = coerceNumber(
        recommendedBreakdown?.weighted_error_hours
      );
      const hasPersistedWeightedMiss = (
        actualWeightedError != null && recommendedWeightedError != null
      );
      const hasPersistedScore = (
        coerceNumber(actualBreakdown?.fit_score) != null ||
        coerceNumber(recommendedBreakdown?.fit_score) != null
      );
      const isScored = targetTotal > 0 && (
        !hasPersistedWeightedMiss || hasPersistedScore
      );
      const actualMissHours = isScored
        ? (hasPersistedWeightedMiss ? actualWeightedError : actualError / 4)
        : null;
      const recommendedMissHours = isScored
        ? (hasPersistedWeightedMiss ? recommendedWeightedError : recommendedError / 4)
        : null;
      const missDeltaHours = isScored
        ? recommendedMissHours - actualMissHours
        : null;

      return {
        roleKey: role.roleKey,
        roleLabel: role.roleLabel,
        exactRoleKeys: Array.from(role.exactRoleKeys),
        chartData,
        targetHours: targetTotal / 4,
        isScored,
        actualScheduledHours: actualScheduledTotal / 4,
        recommendedScheduledHours: recommendedScheduledTotal / 4,
        actualMissHours,
        recommendedMissHours,
        missDeltaHours,
      };
    })
    .filter((role) => role.chartData.length)
    .sort((a, b) => (
      Number(b.targetHours > 0) - Number(a.targetHours > 0) ||
      b.recommendedMissHours - a.recommendedMissHours ||
      b.missDeltaHours - a.missDeltaHours ||
      b.targetHours - a.targetHours ||
      a.roleLabel.localeCompare(b.roleLabel)
    ));
}

function filterRowsForSelectedRole(rows, selectedRole) {
  if (!selectedRole) return rows || [];
  const exactRoleKeys = new Set(selectedRole.exactRoleKeys || []);
  return (rows || []).filter((row) => (
    getRoleTargetDisplayKey(row) === selectedRole.roleKey ||
    exactRoleKeys.has(getRoleTargetKey(row))
  ));
}

function buildDaySummary(run, day) {
  const actualRows = getScheduleRowsForDate(run, "actual", day.date);
  const recommendedRows = getScheduleRowsForDate(run, "recommended", day.date);
  const roleTargetRows = getRoleTargetRowsForDate(run, day.date);
  const actualRoleTargetBreakdown = getDailyRoleTargetBreakdown(
    run,
    "actual",
    day.date
  );
  const recommendedRoleTargetBreakdown = getDailyRoleTargetBreakdown(
    run,
    "recommended",
    day.date
  );
  const actualShiftHours = sumRowsHours(actualRows);
  const recommendedShiftHours = sumRowsHours(recommendedRows);
  const actualHours = sumLaborHours(run?.labor_rows, "actual", day.date);
  const recommendedHours = sumLaborHours(run?.labor_rows, "recommended", day.date);
  const forecastHours = sumLaborHours(run?.labor_rows, "forecasted", day.date);
  const chartData = buildDailyLaborChartData(run, day.date);
  const actualCoverageScore = calculateDailyCoverageScore(chartData, "actual");
  const recommendedCoverageScore = calculateDailyCoverageScore(chartData, "recommended");
  const actualBudgetScore = calculateDailyBudgetScore(actualHours, forecastHours);
  const recommendedBudgetScore = calculateDailyBudgetScore(
    recommendedHours,
    forecastHours
  );
  const actualDailyScore = averageScores([actualCoverageScore, actualBudgetScore]);
  const recommendedDailyScore = averageScores([
    recommendedCoverageScore,
    recommendedBudgetScore,
  ]);
  const roleTargetMiss = getDailyRoleTargetMisses(
    run,
    day.date,
    actualRows,
    recommendedRows,
    roleTargetRows
  );
  const actualExactKeys = new Set(actualRows.map((row) => getShiftExactKey(row)));
  const recommendedExactKeys = new Set(
    recommendedRows.map((row) => getShiftExactKey(row))
  );
  const actualMemberKeys = new Set(actualRows.map((row) => getMemberKey(row)));
  const recommendedMemberKeys = new Set(
    recommendedRows.map((row) => getMemberKey(row))
  );

  let added = 0;
  let changed = 0;
  let removed = 0;
  recommendedRows.forEach((row) => {
    if (actualExactKeys.has(getShiftExactKey(row))) return;
    if (actualMemberKeys.has(getMemberKey(row))) changed += 1;
    else added += 1;
  });
  actualRows.forEach((row) => {
    if (recommendedExactKeys.has(getShiftExactKey(row))) return;
    if (!recommendedMemberKeys.has(getMemberKey(row))) removed += 1;
  });

  return {
    ...day,
    actualRows,
    recommendedRows,
    roleTargetRows,
    actualRoleTargetBreakdown,
    recommendedRoleTargetBreakdown,
    actualShiftHours,
    recommendedShiftHours,
    actualHours,
    recommendedHours,
    forecastHours,
    actualCoverageScore,
    recommendedCoverageScore,
    actualBudgetScore,
    recommendedBudgetScore,
    actualDailyScore,
    recommendedDailyScore,
    actualRoleTargetMissHours: roleTargetMiss.actualHours,
    recommendedRoleTargetMissHours: roleTargetMiss.recommendedHours,
    roleTargetMissIsDiagnostic: roleTargetMiss.isDiagnostic,
    chartData,
    dailyScoreDelta:
      actualDailyScore == null || recommendedDailyScore == null
        ? null
        : recommendedDailyScore - actualDailyScore,
    coverageScoreDelta:
      actualCoverageScore == null || recommendedCoverageScore == null
        ? null
        : recommendedCoverageScore - actualCoverageScore,
    budgetScoreDelta:
      actualBudgetScore == null || recommendedBudgetScore == null
        ? null
        : recommendedBudgetScore - actualBudgetScore,
    roleTargetMissDeltaHours:
      roleTargetMiss.actualHours == null || roleTargetMiss.recommendedHours == null
        ? null
        : roleTargetMiss.recommendedHours - roleTargetMiss.actualHours,
    actualForecastDelta: actualHours - forecastHours,
    recommendedForecastDelta: recommendedHours - forecastHours,
    shiftsDelta: recommendedRows.length - actualRows.length,
    hoursDelta: recommendedHours - actualHours,
    added,
    changed,
    removed,
    changeCount: added + changed + removed,
  };
}

function calculateDailyCoverageScore(chartData, scheduleKey) {
  const forecastLabor = (chartData || []).reduce(
    (total, row) => total + (coerceNumber(row?.forecasted) || 0),
    0
  );
  if (!forecastLabor) return null;
  const uncoveredLabor = (chartData || []).reduce((total, row) => {
    const forecasted = coerceNumber(row?.forecasted) || 0;
    const scheduled = coerceNumber(row?.[scheduleKey]) || 0;
    return total + Math.max(0, forecasted - scheduled);
  }, 0);
  return getNullablePercent(((forecastLabor - uncoveredLabor) / forecastLabor) * 100);
}

function calculateDailyBudgetScore(scheduledHours, forecastHours) {
  const scheduled = coerceNumber(scheduledHours);
  const forecasted = coerceNumber(forecastHours);
  if (scheduled == null || forecasted == null || forecasted <= 0) return null;
  return getNullablePercent((1 - Math.abs(scheduled - forecasted) / forecasted) * 100);
}

function buildDailyLaborChartData(run, date) {
  const rows = (run?.labor_rows || []).filter(
    (row) => row.date === date && ["actual", "recommended", "forecasted"].includes(row.labor_type)
  );
  const byQh = {};
  rows.forEach((row) => {
    const qh = coerceNumber(row.qh_num);
    if (qh == null) return;
    const key = String(qh);
    if (!byQh[key]) {
      byQh[key] = {
        qh,
        label: formatTime(qh),
        actual: 0,
        recommended: 0,
        forecasted: 0,
      };
    }
    byQh[key][row.labor_type] += row.labor_type === "forecasted"
      ? getForecastLaborValue(row)
      : getLaborRowValue(row);
  });
  const sortedRows = Object.values(byQh).sort((a, b) => a.qh - b.qh);
  const activeRows = sortedRows.filter((row) =>
    ["actual", "recommended", "forecasted"].some(
      (key) => (coerceNumber(row?.[key]) || 0) > 0
    )
  );
  if (!activeRows.length) return sortedRows;
  const startQh = Math.max(0, Math.floor(activeRows[0].qh * 2) / 2 - 0.5);
  const endQh = Math.ceil(activeRows[activeRows.length - 1].qh * 2) / 2 + 0.5;
  return sortedRows.filter((row) => row.qh >= startQh && row.qh <= endQh);
}

function getChangeBadge(changeType) {
  if (changeType === "added") {
    return {
      label: "+",
      text: "Added",
      className: "border-emerald-200 bg-emerald-50 text-emerald-700",
    };
  }
  if (changeType === "removed") {
    return {
      label: "-",
      text: "Removed",
      className: "border-rose-200 bg-rose-50 text-rose-700",
    };
  }
  if (changeType === "changed") {
    return {
      label: "~",
      text: "Changed",
      className: "border-amber-200 bg-amber-50 text-amber-700",
    };
  }
  return null;
}

function getRunMetricScore(metrics, metricKey) {
  return coerceNumber(metrics?.[metricKey]);
}

function getRunMetricDelta(run, metricKey) {
  if (!run || !metricKey) return null;
  if (!hasRunSnapshotRows(run)) {
    return coerceNumber(run.metric_deltas?.[metricKey]);
  }
  const currentScore = getRunMetricScore(run.actual_metrics || {}, metricKey);
  const proposedScore = getRunMetricScore(run.recommended_metrics || {}, metricKey);
  if (currentScore != null && proposedScore != null) {
    return proposedScore - currentScore;
  }
  return coerceNumber(run.metric_deltas?.[metricKey]);
}

function shouldShowMetric(metric, run) {
  if (!metric) return false;
  if (metric.requiresKeyholder && run?.debug_summary?.has_keyholder_roles === false) {
    return false;
  }
  if (metric.currentScore == null && metric.proposedScore == null) return false;
  if (!metric.hideWhenNoSignal) return true;
  if (metric.delta != null && Math.abs(metric.delta) >= 0.05) return true;
  return (
    (metric.currentScore != null && metric.currentScore < 99.95) ||
    (metric.proposedScore != null && metric.proposedScore < 99.95)
  );
}

function buildRunScorecardMetrics(run, metricKeys) {
  if (!run) return [];
  const actualMetrics = run.actual_metrics || {};
  const recommendedMetrics = run.recommended_metrics || {};
  return metricKeys.map((metricKey) => {
    const metric = RUN_METRIC_DEFINITION_BY_KEY[metricKey];
    if (!metric) return null;
    const currentScore = getRunMetricScore(actualMetrics, metric.key);
    const proposedScore = getRunMetricScore(recommendedMetrics, metric.key);
    if (currentScore == null && proposedScore == null) return null;
    const delta =
      getRunMetricDelta(run, metric.key) ??
      (currentScore == null || proposedScore == null
        ? null
        : proposedScore - currentScore);
    return {
      ...metric,
      currentScore,
      proposedScore,
      delta,
      actualLabel: formatNumber(currentScore, 1),
      recommendedLabel: formatNumber(proposedScore, 1),
    };
  }).filter((metric) => shouldShowMetric(metric, run));
}

function CompactComparisonBar({ currentScore, proposedScore }) {
  const current = clampPercent(currentScore);
  const proposed = clampPercent(proposedScore ?? currentScore);
  const improved = proposed > current;
  const regressed = proposed < current;
  const changeStart = Math.min(current, proposed);
  const changeWidth = Math.abs(proposed - current);
  const markerLeft = Math.max(2, Math.min(98, proposed));

  return (
    <div className="relative h-2.5">
      <div className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 overflow-hidden rounded-full bg-slate-200">
        <div
          className="absolute left-0 top-0 h-full rounded-full bg-slate-500"
          style={{ width: `${current}%` }}
        />
        {changeWidth > 0 && (
          <div
            className={`absolute top-0 h-full rounded-full ${
              improved ? "bg-emerald-400" : "bg-rose-400"
            }`}
            style={{
              left: `${changeStart}%`,
              width: `${changeWidth}%`,
            }}
          />
        )}
        {regressed && (
          <div
            className="absolute top-0 h-full rounded-full bg-white/40"
            style={{
              left: `${proposed}%`,
              width: `${changeWidth}%`,
            }}
          />
        )}
      </div>
      <div
        className="absolute top-1/2 h-3 w-3 rounded-full border-2 border-white bg-slate-900 shadow-sm"
        style={{
          left: `${markerLeft}%`,
          transform: "translate(-50%, -50%)",
        }}
      />
    </div>
  );
}

function ScoreComparisonBlock({
  label,
  score,
  hours,
  forecastHours,
  accent = "#64748b",
}) {
  return (
    <div className="min-w-0 rounded-xl border border-slate-200 bg-white px-4 py-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-[160px] items-center gap-3">
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${getScoreGradeClass(score)}`}
          >
            {getScoreGrade(score)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-lg font-bold leading-none text-slate-900">
              {formatNumber(score, 1)}
            </div>
          </div>
        </div>
        <LaborRatioSummary
          hours={hours}
          forecastHours={forecastHours}
          toneColor={accent}
          className="min-w-[220px] flex-1"
        />
      </div>
    </div>
  );
}

function RunScoreComparison({ run }) {
  const actualScore = coerceNumber(run?.actual_metrics?.score);
  const recommendedScore = coerceNumber(run?.recommended_metrics?.score);
  const delta = coerceNumber(run?.metric_deltas?.score);
  const actualHours = sumLaborHours(run?.labor_rows, "actual");
  const recommendedHours = sumLaborHours(run?.labor_rows, "recommended");
  const forecastHours = sumLaborHours(run?.labor_rows, "forecasted");
  const actualForecastDelta = actualHours - forecastHours;
  const recommendedForecastDelta = recommendedHours - forecastHours;
  const recommendedHoursDelta = recommendedHours - actualHours;
  const deltaBadgeTone = getDeltaBadgeTone(delta);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">
            Schedule Comparison
          </h3>
          <div className="text-sm text-slate-500">
            Actual schedule vs recommended autoschedule.
          </div>
        </div>
        <span className={`rounded-full px-3 py-1 text-sm font-semibold ${deltaBadgeTone}`}>
          {formatDelta(delta)} pts
        </span>
      </div>
      <div className="mt-4 grid items-stretch gap-3" style={{ gridTemplateColumns: "minmax(0,1fr) 34px minmax(0,1fr)" }}>
        <ScoreComparisonBlock
          label="Actual"
          score={actualScore}
          hours={actualHours}
          forecastHours={forecastHours}
          accent="#64748b"
        />
        <div className="flex items-center justify-center text-slate-400">
          <ArrowForwardIcon fontSize="small" />
        </div>
        <ScoreComparisonBlock
          label="Recommended"
          score={recommendedScore}
          hours={recommendedHours}
          forecastHours={forecastHours}
          accent="#0f766e"
        />
      </div>
      <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Forecast Target
          </div>
          <div className="mt-0.5 text-sm font-bold text-slate-900">
            {formatNumber(forecastHours, 1)} hrs
          </div>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Actual Delta
          </div>
          <div className={`mt-0.5 text-sm font-bold ${getForecastDeltaTone(actualForecastDelta)}`}>
            {formatDelta(actualForecastDelta)} hrs
          </div>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Recommended Delta
          </div>
          <div className={`mt-0.5 text-sm font-bold ${getForecastDeltaTone(recommendedForecastDelta)}`}>
            {formatDelta(recommendedForecastDelta)} hrs
          </div>
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Hours Changed
          </div>
          <div className="mt-0.5 text-sm font-bold text-slate-700">
            {formatDelta(recommendedHoursDelta)} hrs
          </div>
        </div>
      </div>
    </div>
  );
}

function MetricTile({ metric }) {
  const badgeTone = getDeltaBadgeTone(metric.delta);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" title={metric.explanation}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-900">
            {metric.label}
          </div>
          <div className="mt-0.5 truncate text-[11px] text-slate-400">
            {metric.actualLabel} to {metric.recommendedLabel}
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${badgeTone}`}>
          {formatDelta(metric.delta)}
        </span>
      </div>
      <div className="mt-3">
        <CompactComparisonBar
          currentScore={metric.currentScore}
          proposedScore={metric.proposedScore}
        />
      </div>
    </div>
  );
}

function formatCountDelta(value) {
  const numeric = coerceNumber(value);
  if (numeric == null) return "-";
  const prefix = numeric > 0 ? "+" : "";
  return `${prefix}${numeric.toFixed(0)}`;
}

function WarningCountStat({ label, actual, recommended, delta }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className="text-sm font-bold text-slate-900">
          {formatNumber(actual, 0)} -> {formatNumber(recommended, 0)}
        </span>
        <span className={`text-xs font-bold ${getDeltaTone(delta, false)}`}>
          {formatCountDelta(delta)}
        </span>
      </div>
    </div>
  );
}

function buildWarningsScoreFallback(run) {
  const actualScore = coerceNumber(run?.actual_metrics?.schedule_warnings_score);
  const recommendedScore = coerceNumber(run?.recommended_metrics?.schedule_warnings_score);
  if (actualScore == null && recommendedScore == null) return null;
  const delta =
    coerceNumber(run?.metric_deltas?.schedule_warnings_score) ??
    (actualScore == null || recommendedScore == null
      ? null
      : recommendedScore - actualScore);
  return {
    key: "schedule_warnings_score",
    label: "Warnings Score",
    explanation: "Legacy saved run. Regenerate the eval to show warning counts.",
    currentScore: actualScore,
    proposedScore: recommendedScore,
    delta,
    actualLabel: formatNumber(actualScore, 1),
    recommendedLabel: formatNumber(recommendedScore, 1),
  };
}

function WarningCountsTile({ run }) {
  const summary = run?.debug_summary?.schedule_warning_summary;
  if (!summary) {
    const fallbackMetric = buildWarningsScoreFallback(run);
    return fallbackMetric ? <MetricTile metric={fallbackMetric} /> : null;
  }
  const actual = summary.actual || {};
  const recommended = summary.recommended || {};
  const deltas = summary.deltas || {};
  const totalDelta =
    coerceNumber(deltas.total_warnings) ??
    ((coerceNumber(recommended.total_warnings) || 0) -
      (coerceNumber(actual.total_warnings) || 0));

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate text-sm font-semibold text-slate-900">
            Warnings
          </div>
          <div className="mt-0.5 truncate text-[11px] text-slate-400">
            {formatNumber(actual.total_warnings, 0)} to {formatNumber(recommended.total_warnings, 0)} total
          </div>
        </div>
        <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${getDeltaBadgeTone(totalDelta, false)}`}>
          {formatCountDelta(totalDelta)}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <WarningCountStat
          label="Availability"
          actual={actual.availability_conflicts}
          recommended={recommended.availability_conflicts}
          delta={deltas.availability_conflicts}
        />
        <WarningCountStat
          label="Labor Law"
          actual={actual.labor_warnings}
          recommended={recommended.labor_warnings}
          delta={deltas.labor_warnings}
        />
        <WarningCountStat
          label="People"
          actual={actual.impacted_people}
          recommended={recommended.impacted_people}
          delta={deltas.impacted_people}
        />
      </div>
    </div>
  );
}

function MetricSection({ title, subtitle, metrics, children }) {
  const childCards = React.Children.toArray(children).filter(Boolean);
  if (!metrics.length && !childCards.length) return null;

  return (
    <div>
      <div className="mb-2">
        <h3 className="text-sm font-bold text-slate-900">{title}</h3>
        {subtitle ? (
          <div className="text-xs text-slate-500">{subtitle}</div>
        ) : null}
      </div>
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
      >
        {metrics.map((metric) => (
          <MetricTile key={metric.key} metric={metric} />
        ))}
        {childCards}
      </div>
    </div>
  );
}

function buildChartData(runs) {
  return (runs || [])
    .slice()
    .reverse()
    .map((run, index) => {
      const row = {
        uuid: run.uuid,
        label: getRunName(run, index),
        created_at: run.created_at,
        status: run.status,
      };
      CHART_METRICS.forEach((metric) => {
        row[metric.key] = getRunMetricDelta(run, metric.key);
      });
      return row;
    });
}

function RunTrendTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
      <div className="font-semibold text-slate-900">{row.label}</div>
      <div className="text-slate-500">{formatDateTime(row.created_at)}</div>
      <div className="mt-2 space-y-1">
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-4">
            <span style={{ color: entry.color }}>{entry.name}</span>
            <span className="font-medium text-slate-700">
              {formatDelta(entry.value, 1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RunTrendChart({ runs }) {
  const chartData = useMemo(() => buildChartData(runs), [runs]);

  return (
    <div className="border border-slate-200 rounded-lg bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">Eval Movement</h3>
          <div className="text-xs text-slate-500">
            Recommended minus actual deltas across saved runs for this store-week.
          </div>
        </div>
        <div className="text-xs text-slate-500">{runs.length} saved runs</div>
      </div>
      {chartData.length ? (
        <div className="mt-4 h-[280px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#64748b" }}
                stroke="#cbd5e1"
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                stroke="#cbd5e1"
                tickFormatter={formatAxisDelta}
              />
              <Tooltip content={<RunTrendTooltip />} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {CHART_METRICS.map((metric) => (
                <Line
                  key={metric.key}
                  type="monotone"
                  dataKey={metric.key}
                  name={metric.name}
                  stroke={metric.color}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  connectNulls
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
          No eval runs have been saved for this store-week yet.
        </div>
      )}
    </div>
  );
}

function RunTable({ runs, selectedRunUuid, onOpenRun }) {
  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-900">Saved Runs</h3>
        <div className="text-xs text-slate-500">
          Multiple runs can share the same store-week for local comparisons.
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left font-semibold px-4 py-2">Created</th>
              <th className="text-left font-semibold px-4 py-2">Label</th>
              <th className="text-left font-semibold px-4 py-2">Status</th>
              <th className="text-right font-semibold px-4 py-2">Score</th>
              <th className="text-right font-semibold px-4 py-2">Role Fit</th>
              <th className="text-right font-semibold px-4 py-2">Coverage</th>
              <th className="text-right font-semibold px-4 py-2">Budget</th>
              <th className="text-right font-semibold px-4 py-2">Weight</th>
              <th className="text-left font-semibold px-4 py-2">Config</th>
            </tr>
          </thead>
          <tbody>
            {runs.map((run, index) => {
              const selected = run.uuid === selectedRunUuid;
              const scoreDelta = getRunMetricDelta(run, "score");
              const roleTargetDelta = getRunMetricDelta(run, "role_target_fit_score");
              const coverageDelta = getRunMetricDelta(run, "coverage_score");
              const budgetDelta = getRunMetricDelta(run, "budget_score");
              return (
                <tr
                  key={run.uuid}
                  role="button"
                  tabIndex={0}
                  className={`border-t border-slate-100 cursor-pointer ${
                    selected ? "bg-teal-50" : "hover:bg-slate-50"
                  }`}
                  onClick={() => onOpenRun(run.uuid)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onOpenRun(run.uuid);
                    }
                  }}
                >
                  <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                    {formatDateTime(run.created_at)}
                  </td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-slate-900">{getRunName(run, index)}</div>
                    <div className="text-xs text-slate-500">{run.lock_type || "unlocked"}</div>
                  </td>
                  <td className="px-4 py-2">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusTone(run.status)}`}>
                      {run.status || "-"}
                    </span>
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${getDeltaTone(scoreDelta)}`}>
                    {formatDelta(scoreDelta)}
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${getDeltaTone(roleTargetDelta)}`}>
                    {formatDelta(roleTargetDelta)}
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${getDeltaTone(coverageDelta)}`}>
                    {formatDelta(coverageDelta)}
                  </td>
                  <td className={`px-4 py-2 text-right font-medium ${getDeltaTone(budgetDelta)}`}>
                    {formatDelta(budgetDelta)}
                  </td>
                  <td className="px-4 py-2 text-right text-slate-700">
                    {getRoleTargetWeight(run)}
                  </td>
                  <td className="px-4 py-2 text-xs text-slate-500 whitespace-nowrap">
                    {shortHash(run.config_hash)}
                  </td>
                </tr>
              );
            })}
            {!runs.length && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-slate-500">
                  No saved autoschedule eval runs for this week.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RunContextPills({ run }) {
  const sourceSummary = run?.debug_summary?.role_target_history_source_summary || {};
  const selectedShiftCounts = sourceSummary.selected_shift_counts || {};
  const scheduleCounts = countBy(run?.schedule_rows, "schedule_type");
  const laborCounts = countBy(run?.labor_rows, "labor_type");
  const sourceText = Object.entries(selectedShiftCounts).length
    ? Object.entries(selectedShiftCounts)
        .map(([source, count]) => `${SOURCE_LABELS[source] || source}: ${count}`)
        .join(" / ")
    : "No role history source";
  const pills = [
    { label: "Status", value: run?.status || "-" },
    {
      label: "Elapsed",
      value: run?.elapsed_seconds == null ? "-" : `${formatNumber(run.elapsed_seconds, 1)}s`,
    },
    { label: "Weight", value: getRoleTargetWeight(run) },
    { label: "Config", value: shortHash(run?.config_hash) },
    {
      label: "Rows",
      value: `${scheduleCounts.actual || 0}/${scheduleCounts.recommended || 0} shifts`,
    },
    {
      label: "Labor",
      value: `${laborCounts.actual || 0}/${laborCounts.recommended || 0}/${laborCounts.role_optimal || 0}`,
    },
  ];

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      {pills.map((pill) => (
        <div
          key={pill.label}
          className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs"
        >
          <span className="font-semibold text-slate-400">{pill.label}</span>
          <span className="ml-1 font-semibold text-slate-700">{pill.value}</span>
        </div>
      ))}
      <div
        className="min-w-0 truncate rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-500"
        title={sourceText}
      >
        {sourceText}
      </div>
    </div>
  );
}

function sumDaySummaryField(daySummaries, field) {
  return (daySummaries || []).reduce(
    (total, day) => total + (coerceNumber(day?.[field]) || 0),
    0
  );
}

function GradeScore({ score, label }) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold ${getScoreGradeClass(
          score
        )}`}
      >
        {getScoreGrade(score)}
      </span>
      <span className="text-lg font-bold leading-none text-slate-900">
        {formatNumber(score, 0)}
      </span>
      {label ? (
        <span className="sr-only">{label}</span>
      ) : null}
    </div>
  );
}

function DayMetricRow({ label, actualScore, recommendedScore, delta }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
        <span className="font-semibold text-slate-500">{label}</span>
        <span className="flex items-center gap-1 font-semibold">
          <span className="text-slate-900">{formatNumber(recommendedScore, 0)}</span>
          <span className={getDeltaTone(delta)}>{formatDelta(delta, 0)}</span>
        </span>
      </div>
      <CompactComparisonBar
        currentScore={actualScore}
        proposedScore={recommendedScore}
      />
    </div>
  );
}

function DayDiagnosticRow({ label, value }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-3">
      <div className="text-[11px] font-semibold text-slate-500">
        {label}
      </div>
      <div className="whitespace-nowrap text-xs font-bold text-slate-900">{value}</div>
    </div>
  );
}

function DayLaborDiagnostics({ day }) {
  const actualDeltaAbs = Math.abs(coerceNumber(day.actualForecastDelta) || 0);
  const recommendedDeltaAbs = Math.abs(coerceNumber(day.recommendedForecastDelta) || 0);
  const improvement = actualDeltaAbs - recommendedDeltaAbs;
  const improvementText = Math.abs(improvement) > 0.05
    ? improvement > 0
      ? `${formatNumber(improvement, 1)} hrs closer to forecast`
      : `${formatNumber(Math.abs(improvement), 1)} hrs farther from forecast`
    : "same distance from forecast";

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      <div className="mb-2 flex items-center justify-between gap-2 border-b border-slate-100 pb-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400">
          Labor
        </span>
        <span className="text-[11px] font-semibold text-slate-500">
          {improvementText}
        </span>
      </div>
      <div className="space-y-1.5">
        <DayDiagnosticRow
          label="Forecast target"
          value={`${formatNumber(day.forecastHours, 1)} hrs`}
        />
        <DayDiagnosticRow
          label="Actual vs forecast"
          value={`${formatDelta(day.actualForecastDelta)} hrs`}
        />
        <DayDiagnosticRow
          label="Recommended vs forecast"
          value={`${formatDelta(day.recommendedForecastDelta)} hrs`}
        />
      </div>
    </div>
  );
}

function DayScheduleDiagnostics({ day }) {
  return (
    <div className="mt-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5">
      <div className="space-y-1.5">
        <DayDiagnosticRow
          label="Shift Count"
          value={`${day.actualRows.length} -> ${day.recommendedRows.length}`}
        />
        <DayDiagnosticRow
          label="Changed shifts"
          value={`${day.added + day.changed + day.removed} total`}
        />
      </div>
      <div className="mt-2 text-[11px] font-semibold leading-snug text-slate-500">
        {day.added} added · {day.changed} changed · {day.removed} removed
      </div>
    </div>
  );
}

function DayScoreStrip({ daySummaries, selectedDate, onSelectDate }) {
  return (
    <div
      className="grid min-w-0 gap-3 overflow-x-auto pb-1"
      style={{
        gridTemplateColumns: `repeat(${Math.max(daySummaries.length, 1)}, minmax(260px, 1fr))`,
      }}
    >
      {daySummaries.map((day) => {
        const active = day.date === selectedDate;
        const deltaBadgeTone = getDeltaBadgeTone(day.dailyScoreDelta);
        return (
          <button
            key={day.date}
            type="button"
            className={`min-w-0 rounded-xl border p-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-500 ${
              active
                ? "border-violet-500 bg-violet-50"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
            onClick={() => onSelectDate(day.date)}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="truncate whitespace-nowrap text-xs font-bold text-slate-900">
                {dayjs(day.date).format("MMM D, YYYY")}
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                  deltaBadgeTone
                }`}
              >
                {formatDelta(day.dailyScoreDelta, 1)} pts
              </span>
            </div>
            <div className="mt-4 grid min-w-0 grid-cols-[minmax(0,1fr)_20px_minmax(0,1fr)] items-center gap-2">
              <GradeScore score={day.actualDailyScore} label="Actual daily score" />
              <ArrowForwardIcon
                className="justify-self-center text-slate-400"
                style={{ fontSize: "18px" }}
              />
              <GradeScore score={day.recommendedDailyScore} label="Recommended daily score" />
            </div>
            <div className="mt-4 space-y-3">
              <DayMetricRow
                label="Coverage"
                actualScore={day.actualCoverageScore}
                recommendedScore={day.recommendedCoverageScore}
                delta={day.coverageScoreDelta}
              />
              <DayMetricRow
                label="Budget Fit"
                actualScore={day.actualBudgetScore}
                recommendedScore={day.recommendedBudgetScore}
                delta={day.budgetScoreDelta}
              />
            </div>
            <div className="mt-4 border-t border-slate-200 pt-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                Daily Diagnostics
              </div>
              {day.actualRoleTargetMissHours != null ||
              day.recommendedRoleTargetMissHours != null ? (
                <DayDiagnosticRow
                  label={day.roleTargetMissIsDiagnostic
                    ? "Role miss (diagnostic)"
                    : "Weighted role miss"}
                  value={`${formatNumber(day.actualRoleTargetMissHours, 1)} -> ${formatNumber(day.recommendedRoleTargetMissHours, 1)} hrs (${formatDelta(day.roleTargetMissDeltaHours, 1)})`}
                />
              ) : null}
              <DayLaborDiagnostics day={day} />
              <DayScheduleDiagnostics day={day} />
            </div>
          </button>
        );
      })}
    </div>
  );
}

function LaborChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
      <div className="font-semibold text-slate-900">{label}</div>
      <div className="mt-2 space-y-1">
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-4">
            <span style={{ color: entry.color }}>{entry.name}</span>
            <span className="font-medium text-slate-700">
              {formatNumber(entry.value, 1)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function buildScheduleTimelineRows(rows) {
  const openRows = [];
  const groupedRows = new Map();

  (rows || []).forEach((row, index) => {
    if (isOpenScheduleRow(row)) {
      openRows.push({
        key: `open-${row.uuid || index}`,
        label: "Open shift",
        hours: getScheduleRowHours(row),
        firstStart: getShiftStart(row) ?? 0,
        primaryRow: row,
        shifts: [row],
        isOpen: true,
      });
      return;
    }

    const groupKey = getMemberKey(row);
    if (!groupedRows.has(groupKey)) {
      groupedRows.set(groupKey, {
        key: groupKey,
        label: getScheduleRowPersonLabel(row),
        hours: 0,
        firstStart: getShiftStart(row) ?? 0,
        primaryRow: row,
        shifts: [],
        isOpen: false,
      });
    }
    const group = groupedRows.get(groupKey);
    group.hours += getScheduleRowHours(row);
    group.firstStart = Math.min(group.firstStart, getShiftStart(row) ?? group.firstStart);
    group.shifts.push(row);
  });

  return [
    ...openRows,
    ...Array.from(groupedRows.values()).map((group) => ({
      ...group,
      shifts: group.shifts.sort((a, b) => (getShiftStart(a) || 0) - (getShiftStart(b) || 0)),
    })),
  ].sort((a, b) => a.firstStart - b.firstStart || a.label.localeCompare(b.label));
}

function getScheduleTimelineRange(rows) {
  const timeValues = (rows || [])
    .flatMap((row) => [getShiftStart(row), getShiftEnd(row)])
    .filter((value) => value != null);
  if (!timeValues.length) {
    return { startHour: 6, endHour: 22 };
  }
  const startHour = Math.max(0, Math.floor(Math.min(...timeValues)));
  const endHour = Math.max(startHour + 1, Math.ceil(Math.max(...timeValues)));
  return { startHour, endHour };
}

function ScheduleTimeline({
  rows,
  counterpartRows,
  scheduleType,
  roleColorLookups,
}) {
  const timelineRows = buildScheduleTimelineRows(rows);
  const { startHour, endHour } = getScheduleTimelineRange(rows);
  const gridOffsetPercent = 27;
  const gridWidthPercent = 100 - gridOffsetPercent;
  const hourIncrement = gridWidthPercent / (endHour - startHour);
  const tickInterval = endHour - startHour > 14 ? 2 : 1;

  const getLeftPercent = (value) => {
    const shiftStart = coerceNumber(value);
    if (shiftStart == null) return gridOffsetPercent;
    return gridOffsetPercent + Math.max(0, shiftStart - startHour) * hourIncrement;
  };

  const getWidthPercent = (row) => {
    const start = getShiftStart(row);
    const end = getShiftEnd(row);
    if (start == null || end == null || end <= start) {
      return Math.max(3, getShiftDuration(row) * hourIncrement);
    }
    const visibleStart = Math.max(startHour, start);
    const visibleEnd = Math.min(endHour, end);
    return Math.max(3, (visibleEnd - visibleStart) * hourIncrement);
  };

  if (!timelineRows.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
        No shifts stored for this day.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[620px]">
        <div className="relative h-7 border-b border-slate-200">
          {Array.from({ length: endHour - startHour + 1 }, (_, index) => {
            const hour = startHour + index;
            const isEndpoint = index === 0 || hour === endHour;
            if (!isEndpoint && (hour - startHour) % tickInterval !== 0) {
              return null;
            }
            return (
              <div
                key={`tick-${hour}`}
                className="absolute top-0 text-[11px] text-slate-500"
                style={{
                  left: `${gridOffsetPercent + hourIncrement * index}%`,
                  transform: index === 0 ? "translateX(0)" : "translateX(-50%)",
                }}
              >
                {formatTime(hour)}
              </div>
            );
          })}
        </div>
        <div className="relative border-b border-slate-200">
          {Array.from({ length: endHour - startHour + 1 }, (_, index) => (
            <div
              key={`grid-${index}`}
              className="absolute top-0 h-full border-l border-slate-200"
              style={{ left: `${gridOffsetPercent + hourIncrement * index}%` }}
            />
          ))}
          {timelineRows.map((timelineRow) => (
            <div
              key={timelineRow.key}
              className="relative flex min-h-[48px] items-center border-t border-slate-200 py-2"
            >
              <div
                className="flex-none pl-2 pr-3"
                style={{ width: `${gridOffsetPercent}%` }}
              >
                <div className="flex min-w-0 items-center gap-2">
                  <div
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white ${
                      timelineRow.isOpen
                        ? "bg-slate-400"
                        : getScheduleRowRoleColorClass(
                            timelineRow.primaryRow,
                            roleColorLookups
                          )
                    }`}
                  >
                    {timelineRow.isOpen ? "OS" : buildInitials(timelineRow.label)}
                  </div>
                  <div className="min-w-0">
                    <div className="truncate text-xs font-bold text-slate-900">
                      {timelineRow.label}
                    </div>
                    <div className="text-[11px] font-semibold text-slate-500">
                      {formatNumber(timelineRow.hours, 1)} hrs
                    </div>
                  </div>
                </div>
              </div>
              {timelineRow.shifts.map((row, index) => {
                const shiftStart = getShiftStart(row);
                const shiftDuration = getShiftDuration(row);
                const breakStart = coerceNumber(row?.break_start);
                const breakLength = coerceNumber(row?.break_length);
                const breakOffsetPercent =
                  shiftDuration > 0 && breakStart != null && shiftStart != null
                    ? ((breakStart - shiftStart) / shiftDuration) * 100
                    : 0;
                const breakWidthPercent =
                  shiftDuration > 0 && breakLength != null
                    ? (breakLength / shiftDuration) * 100
                    : 0;
                const badge = getChangeBadge(
                  getShiftChangeType(row, counterpartRows, scheduleType)
                );
                return (
                  <div
                    key={row.uuid || `${timelineRow.key}-${index}`}
                    className={`absolute z-10 flex h-8 min-w-[44px] items-center overflow-hidden whitespace-nowrap rounded-lg px-2.5 text-xs font-semibold text-white shadow-sm ${getScheduleRowRoleColorClass(
                      row,
                      roleColorLookups
                    )}`}
                    style={{
                      left: `${getLeftPercent(shiftStart)}%`,
                      width: `${getWidthPercent(row)}%`,
                    }}
                    title={`${getScheduleRowPersonLabel(row)} · ${formatShiftRange(row)} · ${getScheduleRowRoleLabel(row)}`}
                  >
                    {badge ? (
                      <span
                        className="mr-1.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white/90 text-[10px] font-bold text-slate-800"
                        title={badge.text}
                      >
                        {badge.label}
                      </span>
                    ) : null}
                    <span className="truncate">
                      {formatShiftRange(row)}
                      <span className="ml-2">{getScheduleRowRoleLabel(row)}</span>
                    </span>
                    {breakLength > 0 ? (
                      <span
                        className="absolute top-0 h-8 rounded-lg"
                        style={{
                          width: `${breakWidthPercent}%`,
                          left: `${breakOffsetPercent}%`,
                          backgroundColor: "rgba(0, 0, 0, 0.22)",
                        }}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SchedulePanelLaborChart({
  data,
  targetKey = "forecasted",
  targetLabel = "Forecasted labor",
  scheduleKey,
  scheduleLabel,
  scheduleColor,
}) {
  if (!data?.length) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
        No intraday labor rows were stored for this day.
      </div>
    );
  }

  return (
    <div className="h-[230px]">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="#e2e8f0" />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "#64748b" }}
            interval="preserveStartEnd"
            minTickGap={32}
            stroke="#cbd5e1"
          />
          <YAxis
            tick={{ fontSize: 11, fill: "#64748b" }}
            stroke="#cbd5e1"
            width={32}
          />
          <Tooltip content={<LaborChartTooltip />} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area
            type="stepAfter"
            dataKey={targetKey}
            name={targetLabel}
            stroke="#94a3b8"
            fill="#cbd5e1"
            fillOpacity={0.7}
            dot={false}
            isAnimationActive={false}
          />
          <Line
            type="stepAfter"
            dataKey={scheduleKey}
            name={scheduleLabel}
            stroke={scheduleColor}
            strokeWidth={2.5}
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function LaborRatioSummary({
  hours,
  forecastHours,
  toneColor,
  label = "Labor",
  denominatorLabel = "forecasted",
  className = "min-w-[220px]",
  isScored = true,
}) {
  const hasTarget = isScored && forecastHours > 0;
  const ratio = hasTarget ? (hours / forecastHours) * 100 : null;
  const overForecast = ratio != null && ratio > 105;
  const underForecast = ratio != null && ratio < 95;
  const ratioTextTone = !hasTarget
    ? "text-slate-500"
    : overForecast
    ? "text-rose-700"
    : underForecast
    ? "text-amber-700"
    : "text-emerald-700";
  const fillTone = !hasTarget
    ? "bg-slate-300"
    : overForecast
    ? "bg-rose-500"
    : underForecast
    ? "bg-amber-500"
    : "bg-emerald-500";
  const ratioLabel = !isScored
    ? "Not scored"
    : ratio == null
    ? forecastHours > 0
      ? `- of ${denominatorLabel}`
      : "No target hours"
    : `${formatPercent(ratio)} of ${denominatorLabel}`;

  return (
    <div className={className}>
      <div className="mb-1 flex items-center justify-between gap-3">
        <span className={`text-sm font-bold ${ratioTextTone}`}>
          {formatNumber(hours, 1)} / {formatNumber(forecastHours, 1)} hrs
        </span>
        <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
          {label}
        </span>
      </div>
      <div className="relative h-2.5 overflow-hidden rounded-full bg-slate-200">
        <div
          className={`h-full rounded-full ${fillTone}`}
          style={{ width: `${Math.min(100, Math.max(0, ratio || 0))}%` }}
        />
        {hasTarget ? (
          <div
            className="absolute top-0 h-full w-0.5 bg-slate-900/70"
            style={{ left: "calc(100% - 1px)" }}
            title={`${label} target`}
          />
        ) : null}
      </div>
      <div className={`mt-1 text-xs font-semibold ${ratioTextTone}`}>
        {ratioLabel}
      </div>
      <div className="sr-only" style={{ color: toneColor }}>
        Schedule labor compared to {denominatorLabel}
      </div>
    </div>
  );
}

function SchedulePanel({
  title,
  daySummary,
  scheduleType,
  roleColorLookups,
  selectedRole = null,
}) {
  const isActual = scheduleType === "actual";
  const allRows = isActual ? daySummary.actualRows : daySummary.recommendedRows;
  const allCounterpartRows = isActual
    ? daySummary.recommendedRows
    : daySummary.actualRows;
  const rows = filterRowsForSelectedRole(allRows, selectedRole);
  const counterpartRows = filterRowsForSelectedRole(allCounterpartRows, selectedRole);
  const hours = selectedRole
    ? isActual
      ? selectedRole.actualScheduledHours
      : selectedRole.recommendedScheduledHours
    : isActual
      ? daySummary.actualHours
      : daySummary.recommendedHours;
  const shiftHours = sumRowsHours(rows);
  const targetHours = selectedRole ? selectedRole.targetHours : daySummary.forecastHours;
  const scheduleKey = isActual ? "actual" : "recommended";
  const scheduleLabel = isActual ? "Current scheduled" : "Recommended scheduled";
  const scheduleColor = isActual ? "#6d28d9" : "#0f766e";
  const chartData = selectedRole ? selectedRole.chartData : daySummary.chartData;
  const targetKey = selectedRole ? "target" : "forecasted";
  const targetLabel = selectedRole ? "Role target" : "Forecasted labor";
  const panelContextLabel = selectedRole ? selectedRole.roleLabel : "All roles";
  const hoursLabel = selectedRole ? "scheduled role hrs" : "scheduled labor hrs";

  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          <div className="text-sm text-slate-500">
            {panelContextLabel} · {rows.length} shifts · {formatNumber(hours, 1)} {hoursLabel}
          </div>
          {Math.abs((shiftHours || 0) - (hours || 0)) > 0.05 ? (
            <div className="mt-0.5 text-xs text-slate-400">
              {formatNumber(shiftHours, 1)} raw shift hrs
            </div>
          ) : null}
        </div>
        <LaborRatioSummary
          hours={hours}
          forecastHours={targetHours}
          toneColor={scheduleColor}
          label={selectedRole ? "Role Target" : "Labor"}
          denominatorLabel={selectedRole ? "target" : "forecasted"}
          isScored={!selectedRole || selectedRole.isScored}
        />
      </div>
      <div className="mt-4 border-t border-slate-200 pt-4">
        <SchedulePanelLaborChart
          data={chartData}
          targetKey={targetKey}
          targetLabel={targetLabel}
          scheduleKey={scheduleKey}
          scheduleLabel={scheduleLabel}
          scheduleColor={scheduleColor}
        />
      </div>
      <div className="mt-4 border-t border-slate-200 pt-4">
        <ScheduleTimeline
          rows={rows}
          counterpartRows={counterpartRows}
          scheduleType={scheduleType}
          roleColorLookups={roleColorLookups}
        />
      </div>
    </div>
  );
}

function getDayScheduleRows(daySummary, scheduleType) {
  return scheduleType === "actual"
    ? daySummary.actualRows || []
    : daySummary.recommendedRows || [];
}

function buildWeeklyScheduleRows(daySummaries, scheduleType) {
  const rowMap = new Map();

  (daySummaries || []).forEach((day) => {
    getDayScheduleRows(day, scheduleType).forEach((row) => {
      const isOpen = isOpenScheduleRow(row);
      const groupKey = isOpen ? "open-shifts" : getMemberKey(row);
      if (!rowMap.has(groupKey)) {
        rowMap.set(groupKey, {
          key: groupKey,
          label: isOpen ? "Open Shifts" : getScheduleRowPersonLabel(row),
          hours: 0,
          shiftCount: 0,
          firstStart: getShiftStart(row) ?? 0,
          primaryRow: row,
          isOpen,
          dayRows: {},
        });
      }
      const group = rowMap.get(groupKey);
      if (!group.dayRows[day.date]) {
        group.dayRows[day.date] = [];
      }
      group.dayRows[day.date].push(row);
      group.hours += getScheduleRowHours(row);
      group.shiftCount += 1;
      group.firstStart = Math.min(group.firstStart, getShiftStart(row) ?? group.firstStart);
    });
  });

  return Array.from(rowMap.values())
    .map((group) => ({
      ...group,
      dayRows: Object.fromEntries(
        Object.entries(group.dayRows).map(([date, rows]) => [
          date,
          rows.sort((a, b) => (getShiftStart(a) || 0) - (getShiftStart(b) || 0)),
        ])
      ),
    }))
    .sort((a, b) => {
      if (a.isOpen !== b.isOpen) return a.isOpen ? 1 : -1;
      return a.label.localeCompare(b.label) || a.firstStart - b.firstStart;
    });
}

function WeeklyShiftPill({ row, roleColorLookups }) {
  const roleLabel = getScheduleRowRoleLabel(row);

  return (
    <div
      className={`truncate rounded px-1.5 py-0.5 text-center text-[10px] font-semibold text-white ${getScheduleRowRoleColorClass(
        row,
        roleColorLookups
      )}`}
      title={`${getScheduleRowPersonLabel(row)} · ${formatShiftRange(row)} · ${getScheduleRowRoleLabel(row)}`}
    >
      {formatCompactShiftRange(row)} · {roleLabel}
    </div>
  );
}

function WeeklyScheduleGrid({
  title,
  scheduleType,
  daySummaries,
  roleColorLookups,
}) {
  const weeklyRows = buildWeeklyScheduleRows(daySummaries, scheduleType);
  const allRows = (daySummaries || []).flatMap((day) =>
    getDayScheduleRows(day, scheduleType)
  );
  const laborField = scheduleType === "actual" ? "actualHours" : "recommendedHours";
  const laborHours = sumDaySummaryField(daySummaries, laborField);
  const shiftHours = sumRowsHours(allRows);

  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          <div className="text-sm text-slate-500">
            {allRows.length} shifts · {formatNumber(laborHours, 1)} scheduled labor hrs
          </div>
          {Math.abs(shiftHours - laborHours) > 0.05 ? (
            <div className="mt-0.5 text-xs text-slate-400">
              {formatNumber(shiftHours, 1)} raw shift hrs
            </div>
          ) : null}
        </div>
      </div>
      <div className="mt-4 overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full min-w-[900px] table-fixed text-xs">
          <colgroup>
            <col className="w-[180px]" />
            <col className="w-[56px]" />
            {daySummaries.map((day) => (
              <col key={day.date} />
            ))}
          </colgroup>
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-2 py-2 text-left font-semibold">Employee</th>
              <th className="px-2 py-2 text-right font-semibold">Shift Hrs</th>
              {daySummaries.map((day) => (
                <th key={day.date} className="px-1 py-2 text-center font-semibold">
                  {day.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeklyRows.map((row) => (
              <tr key={row.key} className="border-t border-slate-100">
                <td className="px-2 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <div
                      className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white ${
                        row.isOpen
                          ? "bg-slate-400"
                          : getScheduleRowRoleColorClass(row.primaryRow, roleColorLookups)
                      }`}
                    >
                      {row.isOpen ? "OS" : buildInitials(row.label)}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-semibold text-slate-900">
                        {row.label}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {row.shiftCount} shifts
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-2 py-2 text-right font-semibold text-slate-700">
                  {formatNumber(row.hours, 1)}
                </td>
                {daySummaries.map((day) => {
                  const dayRows = row.dayRows[day.date] || [];
                  return (
                    <td key={day.date} className="px-1 py-2 align-top">
                      <div className="flex min-h-[22px] flex-col gap-1">
                        {dayRows.slice(0, 3).map((shift, index) => (
                          <WeeklyShiftPill
                            key={shift.uuid || `${row.key}-${day.date}-${index}`}
                            row={shift}
                            roleColorLookups={roleColorLookups}
                          />
                        ))}
                        {dayRows.length > 3 ? (
                          <div className="text-center text-[10px] font-semibold text-slate-400">
                            +{dayRows.length - 3}
                          </div>
                        ) : null}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
            {!weeklyRows.length ? (
              <tr>
                <td
                  colSpan={daySummaries.length + 2}
                  className="px-3 py-8 text-center text-sm text-slate-500"
                >
                  No shifts stored for this weekly schedule.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function WeeklyScheduleComparison({ daySummaries, roleColorLookups }) {
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-slate-900">Weekly Schedules</h3>
          <div className="text-sm text-slate-500">
            Persisted actual schedule and recommended autoschedule by employee and day.
          </div>
        </div>
      </div>
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(520px, 1fr))" }}
      >
        <WeeklyScheduleGrid
          title="Actual Weekly Schedule"
          scheduleType="actual"
          daySummaries={daySummaries}
          roleColorLookups={roleColorLookups}
        />
        <WeeklyScheduleGrid
          title="Recommended Weekly Schedule"
          scheduleType="recommended"
          daySummaries={daySummaries}
          roleColorLookups={roleColorLookups}
        />
      </div>
    </div>
  );
}

function DailyRoleFilterControl({
  roleOptions,
  selectedRoleKey,
  onSelectRole,
}) {
  if (!roleOptions.length) {
    return null;
  }
  const selectedRole = roleOptions.find((role) => role.roleKey === selectedRoleKey);

  return (
    <div className="mb-4 rounded-2xl border border-slate-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Role Filter</h3>
          <div className="text-xs text-slate-500">
            Filter charts, target bars, and shifts to a specific role.
          </div>
        </div>
        <div className="flex min-w-[240px] flex-wrap items-center gap-2">
          <select
            className="h-9 min-w-[240px] rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-100"
            value={selectedRoleKey}
            onChange={(event) => onSelectRole(event.target.value)}
          >
            <option value="all">All roles</option>
            {roleOptions.map((role) => (
              <option key={role.roleKey} value={role.roleKey}>
                {role.isScored
                  ? `${role.roleLabel} · rec miss ${formatNumber(role.recommendedMissHours, 1)} hrs`
                  : `${role.roleLabel} · not scored`}
              </option>
            ))}
          </select>
        </div>
      </div>
      {selectedRole ? (
        <div className="mt-3 grid gap-2 border-t border-slate-100 pt-3 text-xs" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))" }}>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="font-semibold uppercase tracking-wide text-slate-400">
              Target Hours
            </div>
            <div className="mt-0.5 font-bold text-slate-900">
              {formatNumber(selectedRole.targetHours, 1)}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="font-semibold uppercase tracking-wide text-slate-400">
              Current Scheduled
            </div>
            <div className="mt-0.5 font-bold text-slate-900">
              {formatNumber(selectedRole.actualScheduledHours, 1)}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="font-semibold uppercase tracking-wide text-slate-400">
              Recommended Scheduled
            </div>
            <div className="mt-0.5 font-bold text-slate-900">
              {formatNumber(selectedRole.recommendedScheduledHours, 1)}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <div className="font-semibold uppercase tracking-wide text-slate-400">
              Scored Miss Change
            </div>
            <div className={`mt-0.5 font-bold ${selectedRole.isScored ? getDeltaTone(selectedRole.missDeltaHours, false) : "text-slate-500"}`}>
              {selectedRole.isScored
                ? `${formatDelta(selectedRole.missDeltaHours)} hrs vs current`
                : "Not scored"}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function DailyScheduleComparison({ daySummary, roleColorLookups }) {
  const [selectedRoleKey, setSelectedRoleKey] = useState("all");
  const roleOptions = useMemo(
    () => buildDailyRoleTargetOptions(daySummary),
    [daySummary]
  );
  const selectedRole = selectedRoleKey === "all"
    ? null
    : roleOptions.find((role) => role.roleKey === selectedRoleKey) || null;

  useEffect(() => {
    if (!roleOptions.length) {
      setSelectedRoleKey("all");
      return;
    }
    setSelectedRoleKey((currentRoleKey) => (
      currentRoleKey === "all" ||
      roleOptions.some((role) => role.roleKey === currentRoleKey)
        ? currentRoleKey
        : "all"
    ));
  }, [roleOptions]);

  if (!daySummary) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
        Select a day to compare.
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-2">
          <h3 className="text-base font-bold text-slate-900">Daily Comparison</h3>
          <span className="text-sm font-semibold text-slate-500">
            {daySummary.label}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs font-semibold">
          <span className="rounded-full bg-emerald-50 px-2 py-1 text-emerald-700">
            +{daySummary.added} added
          </span>
          <span className="rounded-full bg-amber-50 px-2 py-1 text-amber-700">
            ~{daySummary.changed} changed
          </span>
          <span className="rounded-full bg-rose-50 px-2 py-1 text-rose-700">
            -{daySummary.removed} removed
          </span>
        </div>
      </div>
      <DailyRoleFilterControl
        roleOptions={roleOptions}
        selectedRoleKey={selectedRoleKey}
        onSelectRole={setSelectedRoleKey}
      />
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))" }}
      >
        <SchedulePanel
          title="Current Schedule"
          daySummary={daySummary}
          scheduleType="actual"
          roleColorLookups={roleColorLookups}
          selectedRole={selectedRole}
        />
        <SchedulePanel
          title="Recommended Schedule"
          daySummary={daySummary}
          scheduleType="recommended"
          roleColorLookups={roleColorLookups}
          selectedRole={selectedRole}
        />
      </div>
    </div>
  );
}

function SelectedRunDetail({ run, isLoading }) {
  const [viewMode, setViewMode] = useState("daily");
  const [selectedDate, setSelectedDate] = useState("");
  const weekDays = useMemo(() => buildWeekDays(run), [run]);
  const daySummaries = useMemo(
    () => weekDays.map((day) => buildDaySummary(run, day)),
    [run, weekDays]
  );
  const coreMetrics = useMemo(
    () => buildRunScorecardMetrics(run, CORE_METRIC_KEYS),
    [run]
  );
  const diagnosticMetrics = useMemo(
    () => buildRunScorecardMetrics(run, DIAGNOSTIC_METRIC_KEYS),
    [run]
  );
  const roleColorLookups = useMemo(() => buildRoleColorLookups(run), [run]);
  const activeDate = selectedDate || daySummaries[0]?.date || "";
  const activeDaySummary = daySummaries.find((day) => day.date === activeDate);

  useEffect(() => {
    setViewMode("daily");
  }, [run?.uuid]);

  useEffect(() => {
    if (!daySummaries.length) {
      setSelectedDate("");
      return;
    }
    setSelectedDate((currentDate) => (
      daySummaries.some((day) => day.date === currentDate)
        ? currentDate
        : daySummaries[0].date
    ));
  }, [daySummaries]);

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-500">
        Loading selected run...
      </div>
    );
  }

  if (!run) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white px-4 py-8 text-sm text-slate-500">
        Select or create a run to inspect the stored evaluation.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center rounded-full bg-slate-100 p-1">
          <button
            type="button"
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              viewMode === "daily"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
            onClick={() => setViewMode("daily")}
          >
            Daily Compare
          </button>
          <button
            type="button"
            className={`rounded-full px-4 py-1.5 text-sm transition-colors ${
              viewMode === "weekly"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-600 hover:text-slate-900"
            }`}
            onClick={() => setViewMode("weekly")}
          >
            Weekly Summary
          </button>
        </div>
        <RunContextPills run={run} />
      </div>

      {run?.error ? (
        <div className="rounded-xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {run.error}
        </div>
      ) : null}

      {viewMode === "daily" ? (
        <>
          <DayScoreStrip
            daySummaries={daySummaries}
            selectedDate={activeDate}
            onSelectDate={setSelectedDate}
          />
          <DailyScheduleComparison
            daySummary={activeDaySummary}
            roleColorLookups={roleColorLookups}
          />
        </>
      ) : (
        <>
          <RunScoreComparison run={run} />
          <MetricSection
            title="Core Metrics"
            subtitle="Primary weekly signals for evaluating the recommendation."
            metrics={coreMetrics}
          />
          <MetricSection
            title="Diagnostics"
            subtitle="Secondary quality and stability signals for deciding what to inspect."
            metrics={diagnosticMetrics}
          >
            <WarningCountsTile run={run} />
          </MetricSection>
          <WeeklyScheduleComparison
            daySummaries={daySummaries}
            roleColorLookups={roleColorLookups}
          />
        </>
      )}
    </div>
  );
}

function RunDetailDialog({ run, runName, isLoading, isOpen, onClose }) {
  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          height: "min(920px, calc(100vh - 36px))",
          maxHeight: "calc(100vh - 36px)",
          width: "min(1500px, calc(100vw - 48px))",
        },
      }}
    >
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-slate-400">
              Autoschedule Eval Run
            </div>
            <h3 className="mt-1 truncate text-xl font-semibold text-slate-900">
              Schedule Comparison
            </h3>
            <div className="mt-1 text-sm text-slate-500">
              {runName || (run ? getRunName(run) : "Run Detail")} · {run?.store_name || "Store"} · {run?.week_start || "-"} to {run?.week_end || "-"}
            </div>
          </div>
          <IconButton aria-label="Close run detail" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 px-5 py-4">
        <SelectedRunDetail run={run} isLoading={isLoading} />
      </div>
    </Dialog>
  );
}

export default function AutoscheduleEvalRunsReport({
  report,
  homeStore,
  onBack,
  storePicker,
  storeNames,
}) {
  const weekStartDayIndex = useMemo(
    () => getBusinessWeekStartDayIndex(storeNames, homeStore),
    [storeNames, homeStore]
  );
  const [weekStart, setWeekStart] = useState(() =>
    snapToWeekStart(new Date(), weekStartDayIndex)
  );
  const [runLabel, setRunLabel] = useState("");
  const [lockType, setLockType] = useState("unlocked");
  const [roleTargetFitWeight, setRoleTargetFitWeight] = useState("0.25");
  const [runs, setRuns] = useState([]);
  const [selectedRunUuid, setSelectedRunUuid] = useState("");
  const [selectedRun, setSelectedRun] = useState(null);
  const [isRunDetailOpen, setIsRunDetailOpen] = useState(false);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState("");

  const selectedRunIndex = useMemo(
    () => runs.findIndex((run) => run.uuid === selectedRunUuid),
    [runs, selectedRunUuid]
  );
  const selectedRunName = useMemo(() => {
    if (selectedRunIndex < 0) return "";
    return getRunName(runs[selectedRunIndex], selectedRunIndex);
  }, [runs, selectedRunIndex]);

  useEffect(() => {
    setWeekStart((prev) =>
      snapToWeekStart(getWeekAnchorDate(prev), weekStartDayIndex)
    );
  }, [weekStartDayIndex]);

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + 6);
    return d;
  }, [weekStart]);

  const incrementWeek = useCallback((currentStart, direction) => {
    const next = new Date(currentStart);
    next.setDate(next.getDate() + direction * 7);
    setWeekStart(next);
  }, []);

  const setWeekStartDate = useCallback(
    (newDate) => {
      setWeekStart(snapToWeekStart(newDate, weekStartDayIndex));
    },
    [weekStartDayIndex]
  );

  const fetchRuns = useCallback(
    async (preferredRunUuid = null) => {
      if (!homeStore || !weekStart) return;
      setIsLoadingRuns(true);
      setError("");
      try {
        const response = await getRightworkAPI(API_autoschedule_eval_runs, {
          store_id: homeStore,
          week_start: dayjs(weekStart).format("YYYY-MM-DD"),
          limit: 50,
        });
        const payload = unwrapPayload(response);
        const nextRuns = payload?.runs || [];
        setRuns(nextRuns);
        setSelectedRunUuid((currentUuid) => {
          const candidate = preferredRunUuid || currentUuid;
          if (candidate && nextRuns.some((run) => run.uuid === candidate)) {
            return candidate;
          }
          return "";
        });
      } catch (err) {
        console.error("Error loading autoschedule eval runs", err);
        setError(getErrorMessage(err, "Unable to load saved autoschedule eval runs."));
      } finally {
        setIsLoadingRuns(false);
      }
    },
    [homeStore, weekStart]
  );

  useEffect(() => {
    setSelectedRunUuid("");
    setSelectedRun(null);
    setIsRunDetailOpen(false);
    fetchRuns();
  }, [fetchRuns]);

  useEffect(() => {
    if (!selectedRunUuid || !isRunDetailOpen) {
      setSelectedRun(null);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingDetail(true);
    setError("");
    (async () => {
      try {
        const response = await getRightworkAPI(API_autoschedule_eval_runs, {
          run_uuid: selectedRunUuid,
        });
        if (cancelled) return;
        const payload = unwrapPayload(response);
        const detailedRun = payload?.run || null;
        setSelectedRun(detailedRun);
        if (detailedRun?.uuid) {
          setRuns((currentRuns) => currentRuns.map((run) => (
            run.uuid === detailedRun.uuid ? { ...run, ...detailedRun } : run
          )));
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading autoschedule eval run detail", err);
        setError(getErrorMessage(err, "Unable to load selected autoschedule eval run."));
      } finally {
        if (!cancelled) setIsLoadingDetail(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isRunDetailOpen, selectedRunUuid]);

  const handleOpenRun = useCallback((runUuid) => {
    setSelectedRunUuid(runUuid);
    setIsRunDetailOpen(true);
  }, []);

  const handleCreateRun = useCallback(async () => {
    if (!homeStore || !weekStart) return;
    setIsCreating(true);
    setError("");
    try {
      const roleWeight =
        roleTargetFitWeight === "" ? null : Number(roleTargetFitWeight);
      const response = await postRightworkAPI(API_autoschedule_eval_runs, {
        store_id: homeStore,
        week_start: dayjs(weekStart).format("YYYY-MM-DD"),
        run_label: runLabel || null,
        run_type: "autoschedule",
        lock_type: lockType,
        role_target_fit_weight: Number.isNaN(roleWeight) ? null : roleWeight,
      });
      const payload = unwrapPayload(response);
      const createdRun = payload?.runs?.[0];
      if (createdRun?.uuid) {
        setSelectedRunUuid(createdRun.uuid);
        setIsRunDetailOpen(true);
      }
      await fetchRuns(createdRun?.uuid || null);
    } catch (err) {
      console.error("Error creating autoschedule eval run", err);
      setError(getErrorMessage(err, "Unable to create autoschedule eval run."));
    } finally {
      setIsCreating(false);
    }
  }, [fetchRuns, homeStore, lockType, roleTargetFitWeight, runLabel, weekStart]);

  const filters = (
    <div className="flex flex-wrap items-end gap-3">
      <WeekSelector
        start_date={weekStart}
        end_date={weekEnd}
        increment_week={incrementWeek}
        set_week_start_date={setWeekStartDate}
        businessWeekStartDayIndex={weekStartDayIndex}
      />
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
        Run Label
        <input
          type="text"
          className="h-9 w-[190px] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
          value={runLabel}
          onChange={(event) => setRunLabel(event.target.value)}
          placeholder="optional"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
        Lock Type
        <select
          className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
          value={lockType}
          onChange={(event) => setLockType(event.target.value)}
        >
          <option value="unlocked">Unlocked</option>
          <option value="locked">Locked</option>
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs font-medium text-slate-500">
        Role Fit Weight
        <input
          type="number"
          step="0.05"
          min="0"
          className="h-9 w-[120px] rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900"
          value={roleTargetFitWeight}
          onChange={(event) => setRoleTargetFitWeight(event.target.value)}
        />
      </label>
    </div>
  );

  const actions = (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={<RefreshIcon />}
        onClick={() => fetchRuns()}
        disabled={isLoadingRuns || isCreating}
        sx={{
          borderColor: "#0f766e",
          color: "#0f766e",
          textTransform: "none",
          "&:hover": { borderColor: "#115e59", backgroundColor: "#f0fdfa" },
        }}
      >
        Refresh
      </Button>
      <Button
        variant="contained"
        size="small"
        startIcon={<PlayArrowIcon />}
        onClick={handleCreateRun}
        disabled={!homeStore || isCreating}
        sx={{
          backgroundColor: "#0f766e",
          textTransform: "none",
          "&:hover": { backgroundColor: "#115e59" },
        }}
      >
        {isCreating ? "Running..." : "Run Eval"}
      </Button>
    </>
  );

  return (
    <PrebuiltReportShell
      report={report}
      onBack={onBack}
      filters={filters}
      storePicker={storePicker}
      actions={actions}
      isLoading={isLoadingRuns && !runs.length}
    >
      {error ? (
        <div className="mb-4 border border-rose-200 bg-rose-50 text-rose-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      ) : null}

      <div className="space-y-4">
        <RunTrendChart runs={runs} />
        <RunTable
          runs={runs}
          selectedRunUuid={selectedRunUuid}
          onOpenRun={handleOpenRun}
        />
      </div>
      <RunDetailDialog
        run={selectedRun}
        runName={selectedRunName}
        isLoading={isLoadingDetail}
        isOpen={isRunDetailOpen}
        onClose={() => setIsRunDetailOpen(false)}
      />
    </PrebuiltReportShell>
  );
}
